import type { Except } from '@festina-life/core'
import { type ComponentProps, useMemo } from 'react'
import { useIntl } from 'react-intl'

import { useUiContext } from '../../theme'
import { type InputProps, InputWrapper } from '../inputWrapper/InputWrapper'
import { createInternalWrapperPropsAndId } from '../utility'
import { RawSelect } from './RawSelect'
import { getSortFn, type SortFn } from './Select.utils'

type Props<T> = Except<ComponentProps<typeof RawSelect<T>>, 'renderInput'> &
  InputProps & {
    sort?: 'automatic' | false | SortFn<T>
  }

export const Select = <T,>({
  wrapperProps,
  sort = 'automatic',
  ...fieldProps
}: Props<T>) => {
  const { locale } = useIntl()
  const { currency } = useUiContext()
  const { internalWrapperProps, id, labelId } = createInternalWrapperPropsAndId(
    {
      wrapperProps,
      id: fieldProps.id,
    },
  )
  const sortFn = useMemo(
    () => getSortFn({ sort, locale, currency }),
    [sort, locale, currency],
  )
  return (
    <RawSelect
      aria-labelledby={internalWrapperProps.label ? labelId : undefined}
      {...fieldProps}
      id={internalWrapperProps.label ? id : fieldProps.id}
      sortFn={sortFn}
      renderInput={(input) => (
        <InputWrapper {...internalWrapperProps} labelType="reactAria">
          {input}
        </InputWrapper>
      )}
    />
  )
}
